// ============================================================
// Gerador mínimo de .xlsx (OOXML) — sem biblioteca de planilha.
// ============================================================
// Monta as partes OOXML e empacota com JSZip (já presente em node_modules).
// Resultado: um .xlsx REAL que abre no Excel SEM o aviso de "formato/extensão
// não conferem". A ÚNICA formatação suportada/aplicada é a COR DE FUNDO da célula
// (fill) — sem bordas, sem fonte custom, sem alinhamento. Hyperlinks por célula.
//
// Validado (estrutura ZIP + xmllint das 7 partes). Ver submissions-list-table-v2
// para o consumidor (exportTableExcel).

import * as JSZipNS from 'jszip';

// Interop CJS/ESM robusto: jszip usa `export =` (CommonJS). Conforme o bundler,
// o construtor vem em `.default` ou no próprio namespace.
const JSZip: any = (JSZipNS as any).default || JSZipNS;

export interface XlsxRow {
  values: string[];
  // Cor de fundo ARGB (8 hex, ex.: 'FF99FF99') ou undefined (sem cor).
  fillArgb?: string;
  // colIndex → URL: transforma a célula num hyperlink do Excel.
  links?: { [colIndex: number]: string };
  // colIndex → FÓRMULA (sem o '=' inicial): a célula vira uma fórmula que o Excel calcula ao abrir
  // (ex.: 'ROUND(AVERAGE(C2:C4),2)'). Tem precedência sobre `values[colIndex]`.
  formulas?: { [colIndex: number]: string };
}

// Escape XML: remove caracteres de controle inválidos e escapa metacaracteres.
function esc(value: any): string {
  let out = '';
  const s = String(value ?? '');
  for (let i = 0; i < s.length; i++) {
    const code = s.charCodeAt(i);
    if (code < 0x20 && code !== 0x09 && code !== 0x0A && code !== 0x0D) { continue; }
    const ch = s[i];
    if (ch === '&') { out += '&amp;'; }
    else if (ch === '<') { out += '&lt;'; }
    else if (ch === '>') { out += '&gt;'; }
    else if (ch === '"') { out += '&quot;'; }
    else if (ch === "'") { out += '&apos;'; }
    else if (ch === '\n') { out += '&#10;'; }  // quebra de linha dentro da célula (precisa wrapText)
    else { out += ch; }
  }
  return out;
}

// Índice de coluna (0-based) → letra(s) da coluna no Excel (A, B, ... Z, AA, ...).
function colLetter(index: number): string {
  let s = '';
  let i = index + 1;
  while (i > 0) {
    const m = (i - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    i = Math.floor((i - 1) / 26);
  }
  return s;
}

// `wrapCols`: índices de coluna (0-based) cujas células devem ter quebra de linha
// VISÍVEL (wrapText) — ex.: Autores e Arquivos, com um item por linha.
export async function buildXlsxBlob(
  sheetName: string, headers: string[], rows: XlsxRow[], wrapCols: number[] = [],
  colWidths: number[] = [], centerCols: number[] = [], noFillCols: number[] = [], numCols: number[] = [],
  numFmtCols: { [col: number]: number } = {}, merges: string[] = []
): Promise<Blob> {
  const wrapSet = new Set(wrapCols);
  const centerSet = new Set(centerCols);
  // Colunas que NUNCA recebem a cor de fundo da linha (ex.: a coluna "#").
  const noFillSet = new Set(noFillCols);
  // Colunas cujo valor deve ser célula NUMÉRICA (ex.: a coluna "#") em vez de texto.
  const numSet = new Set(numCols);
  // Formato de número por coluna (numFmtId OOXML embutido; ex.: 2 = "0.00"). O Excel
  // exibe o separador decimal conforme o locale (pt → vírgula).
  const numFmtFor = (c: number) => numFmtCols[c] || 0;

  // --- Fills: 0=none, 1=gray125, depois um por cor distinta (a partir do fillId 2). ---
  const fillList: string[] = [];
  rows.forEach(r => { if (r.fillArgb && !fillList.includes(r.fillArgb)) { fillList.push(r.fillArgb); } });
  const fillId = (argb?: string) => (argb ? fillList.indexOf(argb) + 2 : 0);

  // --- cellXfs sob demanda: cada combinação (fill, wrapText, hcenter) usada vira um
  // xf. Índice 0 = default. TODAS as células têm alinhamento VERTICAL CENTRALIZADO
  // (sem isso o Excel alinha embaixo e sobra espaço no topo das linhas altas); as
  // colunas com quebra somam wrapText="1"; as colunas pedidas somam horizontal
  // centralizado (horizontal="center"). ---
  const align = (wrap: boolean, hcenter: boolean) => {
    const attrs = ['vertical="center"'];
    if (hcenter) { attrs.push('horizontal="center"'); }
    if (wrap) { attrs.push('wrapText="1"'); }
    return `<alignment ${attrs.join(' ')}/>`;
  };
  const xfList: string[] = [`<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" applyAlignment="1">${align(false, false)}</xf>`];
  const xfMap = new Map<string, number>([['0:0:0:0', 0]]);
  const xfFor = (fId: number, wrap: boolean, hcenter: boolean, numFmtId: number = 0): number => {
    const key = `${fId}:${wrap ? 1 : 0}:${hcenter ? 1 : 0}:${numFmtId}`;
    const cached = xfMap.get(key);
    if (cached !== undefined) { return cached; }
    const idx = xfList.length;
    const fillAttr = fId ? ` fillId="${fId}" applyFill="1"` : ' fillId="0"';
    const nfAttr = numFmtId ? ' applyNumberFormat="1"' : '';
    xfList.push(`<xf numFmtId="${numFmtId}" fontId="0"${fillAttr} borderId="0" xfId="0"${nfAttr} applyAlignment="1">${align(wrap, hcenter)}</xf>`);
    xfMap.set(key, idx);
    return idx;
  };

  // --- Worksheet: linhas/células (texto inline), hyperlinks por célula. ---
  const sheetRows: string[] = [];
  const hyperlinks: string[] = [];
  const hyperlinkRels: string[] = [];
  let relId = 0;

  // Cabeçalho: centraliza horizontalmente os mesmos campos pedidos (consistência com
  // o conteúdo) — sem fill, sem wrap.
  sheetRows.push(`<row r="1">${headers.map((h, c) => {
    const sIdx = xfFor(0, false, centerSet.has(c), 0);   // cabeçalho = texto (sem numFmt)
    const sAttr = sIdx ? ` s="${sIdx}"` : '';
    return `<c r="${colLetter(c)}1"${sAttr} t="inlineStr"><is><t xml:space="preserve">${esc(h)}</t></is></c>`;
  }).join('')}</row>`);

  rows.forEach((r, ri) => {
    const rowNum = ri + 2;
    const fId = fillId(r.fillArgb);
    const cells = r.values.map((v, c) => {
      const ref = `${colLetter(c)}${rowNum}`;
      // Coluna sem fill (ex.: "#") ignora a cor da linha.
      const cellFid = noFillSet.has(c) ? 0 : fId;
      // Célula NUMÉRICA quando a coluna é numérica e o valor é um número válido. Aí
      // aplica o numFmt da coluna (ex.: 2 = "0.00"); senão, texto sem numFmt.
      const formula = r.formulas ? r.formulas[c] : undefined;
      const isNum = numSet.has(c) && v !== '' && v != null && !isNaN(Number(v));
      // Fórmula e número usam o numFmt da coluna (o resultado é numérico); o resto sem numFmt.
      const sIdx = xfFor(cellFid, wrapSet.has(c), centerSet.has(c), (formula || isNum) ? numFmtFor(c) : 0);
      const sAttr = sIdx ? ` s="${sIdx}"` : '';
      if (r.links && r.links[c]) {
        relId++;
        hyperlinkRels.push(`<Relationship Id="rId${relId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/hyperlink" Target="${esc(r.links[c])}" TargetMode="External"/>`);
        hyperlinks.push(`<hyperlink ref="${ref}" r:id="rId${relId}"/>`);
      }
      // Célula de FÓRMULA: o Excel calcula ao abrir (sem <v> cacheado → recalcula).
      if (formula) {
        return `<c r="${ref}"${sAttr}><f>${esc(formula)}</f></c>`;
      }
      // Sem `t` = number (inline string nas demais). Sem link nessas colunas.
      if (isNum) {
        return `<c r="${ref}"${sAttr}><v>${Number(v)}</v></c>`;
      }
      // Vazio → célula EM BRANCO (só estilo/fill), NÃO string vazia: assim AVERAGE ignora e
      // SUM/SUMPRODUCT tratam como 0 (string vazia como texto quebraria o SUMPRODUCT).
      if (v === '' || v == null) {
        return `<c r="${ref}"${sAttr}/>`;
      }
      return `<c r="${ref}"${sAttr} t="inlineStr"><is><t xml:space="preserve">${esc(v)}</t></is></c>`;
    }).join('');
    return sheetRows.push(`<row r="${rowNum}">${cells}</row>`);
  });

  // styles.xml depois das células (xfList já populado).
  const fills = [
    '<fill><patternFill patternType="none"/></fill>',
    '<fill><patternFill patternType="gray125"/></fill>',
    ...fillList.map(a => `<fill><patternFill patternType="solid"><fgColor rgb="${a}"/></patternFill></fill>`),
  ];
  const stylesXml =
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
    '<fonts count="1"><font><sz val="11"/><name val="Calibri"/></font></fonts>' +
    `<fills count="${fills.length}">${fills.join('')}</fills>` +
    '<borders count="1"><border/></borders>' +
    '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
    `<cellXfs count="${xfList.length}">${xfList.join('')}</cellXfs>` +
    '</styleSheet>';

  const lastRef = `${colLetter(Math.max(headers.length, 1) - 1)}${rows.length + 1}`;
  const hyperlinksXml = hyperlinks.length ? `<hyperlinks>${hyperlinks.join('')}</hyperlinks>` : '';
  // Larguras de coluna EXPLÍCITAS: sem isso o Excel calcula a altura da linha com
  // base na largura PADRÃO (estreita) das colunas com quebra, gerando linhas bem mais
  // altas que o conteúdo visível. Com larguras definidas, o auto-fit da altura bate
  // com o que aparece. (Unidade Excel = ~caracteres.)
  const colsXml = colWidths.length
    ? `<cols>${colWidths.map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`).join('')}</cols>`
    : '';
  // Células MESCLADAS (mergeCells vem DEPOIS do sheetData e ANTES dos hyperlinks, pela ordem do
  // schema CT_Worksheet). O valor exibido é o da célula superior-esquerda do range.
  const mergeXml = merges.length
    ? `<mergeCells count="${merges.length}">${merges.map(m => `<mergeCell ref="${esc(m)}"/>`).join('')}</mergeCells>`
    : '';
  const sheetXml =
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
    `<dimension ref="A1:${lastRef}"/>${colsXml}` +
    `<sheetData>${sheetRows.join('')}</sheetData>${mergeXml}${hyperlinksXml}</worksheet>`;

  // --- Partes do pacote OOXML ---
  const contentTypes =
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
    '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
    '<Default Extension="xml" ContentType="application/xml"/>' +
    '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
    '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>' +
    '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
    '</Types>';

  const rootRels =
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
    '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
    '</Relationships>';

  const safeName = esc(sheetName).replace(/[\\\/?*\[\]:]/g, ' ').slice(0, 31) || 'Planilha';
  const workbook =
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
    `<sheets><sheet name="${safeName}" sheetId="1" r:id="rId1"/></sheets></workbook>`;

  const workbookRels =
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
    '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>' +
    '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>' +
    '</Relationships>';

  const zip = new JSZip();
  zip.file('[Content_Types].xml', contentTypes);
  zip.file('_rels/.rels', rootRels);
  zip.file('xl/workbook.xml', workbook);
  zip.file('xl/_rels/workbook.xml.rels', workbookRels);
  zip.file('xl/styles.xml', stylesXml);
  zip.file('xl/worksheets/sheet1.xml', sheetXml);
  if (hyperlinkRels.length) {
    zip.file('xl/worksheets/_rels/sheet1.xml.rels',
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
      '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
      hyperlinkRels.join('') + '</Relationships>');
  }

  return zip.generateAsync({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}
